// src/components/home/awareness/HomeAwarenessManager.js

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import RewardCodeAwarenessCampaign from './RewardCodeAwarenessCampaign';
import WalletActivationAwarenessCampaign from './WalletActivationAwarenessCampaign';
import ProfileBannerAwarenessCampaign from './ProfileBannerAwarenessCampaign';

import BarterFiSurveyCampaign from './BarterFiSurveyCampaign';
import AvatarStudioAwarenessCampaign from './AvatarStudioAwarenessCampaign';

import {
  getGlobalAwarenessGapUntil,
  hasGlobalAwarenessGap,
  setGlobalAwarenessGap,
} from './awarenessStorage';

const GLOBAL_POPUP_GAP_MS =
  5 * 60 * 1000;

const CAMPAIGN_REWARD =
  'reward_code';

const CAMPAIGN_WALLET =
  'wallet_activation';

const CAMPAIGN_BANNER =
  'profile_banner';

const CAMPAIGN_SURVEY =
  'barterfi_survey';


const CAMPAIGN_AVATAR = 'avatar_studio';

const STAGE_AVATAR = 'avatar';
const STAGE_REWARD = 'reward';

const STAGE_WALLET = 'wallet';
const STAGE_BANNER = 'banner';
const STAGE_SURVEY = 'survey';
const STAGE_WAITING = 'waiting';
const STAGE_DONE = 'done';

function HomeAwarenessManager({
  user,
  onUserUpdate,
  blocked = false,
}) {
  const [stage, setStage] =
    useState(STAGE_WAITING);

  const [
    activeCampaign,
    setActiveCampaign,
  ] = useState(null);

  const [
    gapCheckVersion,
    setGapCheckVersion,
  ] = useState(0);

  const gapTimerRef = useRef(null);

  const username = String(
    user?.username || ''
  )
    .trim()
    .toLowerCase();

  const clearGapTimer = useCallback(() => {
    if (!gapTimerRef.current) {
      return;
    }

    window.clearTimeout(
      gapTimerRef.current
    );

    gapTimerRef.current = null;
  }, []);

  /**
   * Reset manager state when switching accounts.
   */
  useEffect(() => {
    clearGapTimer();

    setActiveCampaign(null);
    setStage(STAGE_WAITING);

    setGapCheckVersion(
      (current) => current + 1
    );

    return () => {
      clearGapTimer();
    };
  }, [
    username,
    clearGapTimer,
  ]);

  /**
   * Check the shared popup gap.
   *
   * When no global gap exists, begin with Reward.
   */
  useEffect(() => {
    if (!username) {
      return undefined;
    }

    if (blocked) {
      return undefined;
    }

    if (activeCampaign) {
      return undefined;
    }

    clearGapTimer();

    const gapIsActive =
      hasGlobalAwarenessGap(username);

    if (!gapIsActive) {
      setStage(STAGE_REWARD);

      return undefined;
    }

    setStage(STAGE_WAITING);

    const gapUntil =
      getGlobalAwarenessGapUntil(username);

    const remainingMs = Math.max(
      0,
      Number(gapUntil || 0) -
        Date.now()
    );

    gapTimerRef.current =
      window.setTimeout(() => {
        gapTimerRef.current = null;

        setGapCheckVersion(
          (current) => current + 1
        );
      }, remainingMs + 100);

    return () => {
      clearGapTimer();
    };
  }, [
    username,
    blocked,
    activeCampaign,
    gapCheckVersion,
    clearGapTimer,
  ]);

/**
 * Campaign priority:
 *
 * 1. Reward code
 * 2. Wallet activation
 * 3. Avatar Studio
 * 4. Profile banner
 * 5. BarterFi survey
 */

  const handleEligibilityResolved =
    useCallback(
      ({
        campaignKey,
        eligible,
      } = {}) => {
        if (
          campaignKey ===
          CAMPAIGN_REWARD
        ) {
          if (eligible) {
            setActiveCampaign(
              CAMPAIGN_REWARD
            );

            return;
          }

          setActiveCampaign(null);
          setStage(STAGE_WALLET);

          return;
        }



        if (
          campaignKey ===
          CAMPAIGN_WALLET
        ) {
          if (eligible) {
            setActiveCampaign(
              CAMPAIGN_WALLET
            );

            return;
          }

          setActiveCampaign(null);
          setStage(STAGE_AVATAR);

          return;
        }

        if (
          campaignKey ===
          CAMPAIGN_AVATAR
        ) {
          if (eligible) {
            setActiveCampaign(
              CAMPAIGN_AVATAR
            );

            return;
          }

          setActiveCampaign(null);
          setStage(STAGE_BANNER);

          return;
        }



if (
  campaignKey ===
  CAMPAIGN_BANNER
) {
  if (eligible) {
    setActiveCampaign(
      CAMPAIGN_BANNER
    );

    return;
  }

  setActiveCampaign(null);
  setStage(STAGE_SURVEY);

  return;
}

if (
  campaignKey ===
  CAMPAIGN_SURVEY
) {
  if (eligible) {
    setActiveCampaign(
      CAMPAIGN_SURVEY
    );

    return;
  }

  setActiveCampaign(null);
  setStage(STAGE_DONE);
}

      },
      []
    );

  /**
   * Called when a campaign becomes visible.
   */
  const handleCampaignOpened =
    useCallback(
      ({
        campaignKey,
      } = {}) => {


const allowedCampaigns = [
  CAMPAIGN_REWARD,
  CAMPAIGN_WALLET,
  CAMPAIGN_AVATAR,
  CAMPAIGN_BANNER,
  CAMPAIGN_SURVEY,
];

        if (
          !allowedCampaigns.includes(
            campaignKey
          )
        ) {
          return;
        }

        setActiveCampaign(campaignKey);
      },
      []
    );

  /**
   * Apply the shared five-minute gap after:
   *
   * - dismissal
   * - successful completion
   */
  const handleCampaignFinished =
    useCallback(
      ({
        campaignKey,
        outcome,
        action,
        response,
      } = {}) => {
        if (!username) {
          return;
        }

        setGlobalAwarenessGap({
          username,
          durationMs:
            GLOBAL_POPUP_GAP_MS,
        });

        console.log(
          '✅ Home awareness campaign finished:',
          {
            campaignKey,
            outcome,
            action: action || null,
            hasResponse:
              Boolean(response),
          }
        );

        clearGapTimer();

        setActiveCampaign(null);
        setStage(STAGE_WAITING);

        gapTimerRef.current =
          window.setTimeout(() => {
            gapTimerRef.current = null;

            setGapCheckVersion(
              (current) => current + 1
            );
          }, GLOBAL_POPUP_GAP_MS + 100);
      },
      [
        username,
        clearGapTimer,
      ]
    );

  if (!username) {
    return null;
  }

  const awarenessAllowed =
    !blocked;

  const rewardEnabled =
    awarenessAllowed &&
    stage === STAGE_REWARD &&
    (
      activeCampaign === null ||
      activeCampaign ===
        CAMPAIGN_REWARD
    );

  const walletEnabled =
    awarenessAllowed &&
    stage === STAGE_WALLET &&
    (
      activeCampaign === null ||
      activeCampaign ===
        CAMPAIGN_WALLET
    );


  const avatarEnabled =
    awarenessAllowed &&
    stage === STAGE_AVATAR &&
    (
      activeCampaign === null ||
      activeCampaign === CAMPAIGN_AVATAR
    );

  const bannerEnabled =

    awarenessAllowed &&
    stage === STAGE_BANNER &&
    (
      activeCampaign === null ||
      activeCampaign ===
        CAMPAIGN_BANNER
    );

const surveyEnabled =
  awarenessAllowed &&
  stage === STAGE_SURVEY &&
  (
    activeCampaign === null ||
    activeCampaign ===
      CAMPAIGN_SURVEY
  );


  return (
    <>
      <RewardCodeAwarenessCampaign
        user={user}
        enabled={rewardEnabled}
        onEligibilityResolved={
          handleEligibilityResolved
        }
        onOpened={
          handleCampaignOpened
        }
        onFinished={
          handleCampaignFinished
        }
      />

      <WalletActivationAwarenessCampaign
        user={user}
        enabled={walletEnabled}
        onEligibilityResolved={
          handleEligibilityResolved
        }
        onOpened={
          handleCampaignOpened
        }
        onFinished={
          handleCampaignFinished
        }
      />


      <AvatarStudioAwarenessCampaign
        key={`avatar-studio:${username}`}
        user={user}
        enabled={avatarEnabled}
        onEligibilityResolved={
          handleEligibilityResolved
        }
        onOpened={
          handleCampaignOpened
        }
        onFinished={
          handleCampaignFinished
        }
      />

      <ProfileBannerAwarenessCampaign
        user={user}
        onUserUpdate={onUserUpdate}
        enabled={bannerEnabled}
        onEligibilityResolved={
          handleEligibilityResolved
        }
        onOpened={
          handleCampaignOpened
        }
        onFinished={
          handleCampaignFinished
        }
      />



<BarterFiSurveyCampaign
  user={user}
  enabled={surveyEnabled}
  onEligibilityResolved={
    handleEligibilityResolved
  }
  onOpened={
    handleCampaignOpened
  }
  onFinished={
    handleCampaignFinished
  }
/>


    </>
  );
}

export default HomeAwarenessManager;
